import { useMemo } from "react";
import cn from "classnames";
import { CALENDAR_LOCALE } from "@/shared/ui";
import type { Locale } from "@/shared/utils";
import type { CalendarTask } from "./types";
import { toDateKey, isSameDay } from "./calendarUtils";
import CalendarTaskItem from "./CalendarTaskItem";

interface Props {
  currentDate: Date;
  tasksByDate: Map<string, CalendarTask[]>;
  locale: Locale;
  onTaskClick: (task: CalendarTask) => void;
}

export default function CalendarWeekGrid({ currentDate, tasksByDate, locale, onTaskClick }: Props) {
  const weekdays = CALENDAR_LOCALE[locale].weekdays;
  const today = new Date();

  const weekDays = useMemo(() => {
    const dayOfWeek = (currentDate.getDay() + 6) % 7;
    const monday = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - dayOfWeek);
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      days.push(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i));
    }
    return days;
  }, [currentDate]);

  return (
    <div className="flex-1 flex flex-col">
      <div className="grid grid-cols-7">
        {weekdays.map((w, i) => (
          <div key={w} className="py-2 text-center border-b surface-component-stroke">
            <span className="text-label-xs content-base-low">{w}</span>
            <span
              className={cn(
                "ml-1 text-label-xs",
                isSameDay(weekDays[i], today)
                  ? "background-brand-fill text-white radius-full px-1.5 py-0.5"
                  : "content-base-primary",
              )}>
              {weekDays[i].getDate()}
            </span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 flex-1 border-r border-b surface-component-stroke">
        {weekDays.map((day) => {
          const key = toDateKey(day);
          const tasks = tasksByDate.get(key) ?? [];

          return (
            <div key={key} className="p-1 border-t border-l surface-component-stroke flex flex-col">
              {tasks.length > 0 && (
                <div className="flex-1 flex flex-col gap-0.5 overflow-y-auto component-scroll">
                  {tasks.map((task) => (
                    <CalendarTaskItem key={task.id} task={task} onClick={() => onTaskClick(task)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
